/**
 * 前台用户服务
 * 处理用户相关的 API 调用
 *
 * 命名规范：
 * - 前台使用 userService
 * - 后台使用 adminService
 */

import { userApiClient } from "@/lib/userApiClient";

// 用户类型定义
export interface User {
  id: number;
  email: string;
  name: string;
  avatar_url?: string;
  cover_image_url?: string;
  phone?: string;
  bio?: string;
  gender?: string;
  birthday?: string;
  location?: string;
  account_id?: string;
  points?: number;
  unread?: number;
  checked_in?: boolean;
  has_tanks?: boolean;
  has_tools?: boolean;
  last_check_in_date?: string;
  created_at: string;
  updated_at: string;
}

// 用户公开信息类型
export interface UserPublic {
  id: number;
  name: string;
  avatar_url?: string;
  bio?: string;
}

// 用户更新数据类型
export interface UserUpdateData {
  name?: string;
  bio?: string;
  gender?: string;
  birthday?: string;
  location?: string;
  avatar?: File;
}

// 登录凭证类型
export interface LoginCredentials {
  email: string;
  password: string;
  remember_me?: boolean;
}

// 注册数据类型
export interface RegisterData {
  email: string;
  password: string;
  password_confirmation: string;
  name: string;
}

/**
 * 用户服务类
 * 提供所有用户相关的 API 调用方法
 */
class UserService {
  /**
   * 获取当前用户信息
   */
  async getCurrentUser(): Promise<User> {
    const response = await userApiClient.get<{ user: User }>("/users/me");
    return response.user;
  }

  /**
   * 更新当前用户信息
   */
  async updateCurrentUser(data: UserUpdateData): Promise<User> {
    // 如果包含文件上传，使用 FormData
    if (data.avatar) {
      const formData = new FormData();
      if (data.name) formData.append("user[name]", data.name);
      if (data.bio) formData.append("user[bio]", data.bio);
      if (data.gender) formData.append("user[gender]", data.gender);
      if (data.birthday) formData.append("user[birthday]", data.birthday);
      if (data.location) formData.append("user[location]", data.location);
      if (data.avatar) formData.append("user[avatar]", data.avatar);

      const response = await userApiClient.upload<{ user: User }>(
        "/users/me",
        formData,
      );
      return response.user;
    }

    // 否则使用 JSON
    const response = await userApiClient.patch<{ user: User }>("/users/me", {
      user: data,
    });
    return response.user;
  }

  /**
   * 更新封面图
   */
  async updateCoverImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("cover_image", file);
    const response = await userApiClient.upload<{ cover_image_url: string }>(
      "/cover_image",
      formData,
    );
    return response.cover_image_url;
  }

  /**
   * 获取指定用户的公开信息
   */
  async getUserById(id: number): Promise<UserPublic> {
    const response = await userApiClient.get<{ user: UserPublic }>(
      `/users/${id}`,
    );
    return response.user;
  }

  /**
   * 登录
   * 支持双重认证：
   * - Web 端：Session（Devise）
   * - 移动端：JWT Token
   *
   * 响应包含 user 和 auth（tokens）
   */
  async login(credentials: LoginCredentials): Promise<User> {
    const response = await userApiClient.post<{
      user: User;
      auth: {
        access_token: string;
        refresh_token: string;
        expires_in: number;
        token_type: string;
      };
    }>("/auth/login", credentials);

    // 如果有 Token，保存到 localStorage（移动端使用）
    if (response.auth) {
      localStorage.setItem("user_access_token", response.auth.access_token);
      localStorage.setItem("user_refresh_token", response.auth.refresh_token);
      const expiresAt = Date.now() + response.auth.expires_in * 1000;
      localStorage.setItem("user_token_expires", expiresAt.toString());
    }

    return response.user;
  }

  /**
   * 注册新用户
   */
  async register(data: RegisterData): Promise<User> {
    const response = await userApiClient.post<{
      user: User;
      auth: {
        access_token: string;
        refresh_token: string;
        expires_in: number;
        token_type: string;
      };
    }>("/auth/register", data);

    // 如果有 Token，保存到 localStorage（移动端使用）
    if (response.auth) {
      localStorage.setItem("user_access_token", response.auth.access_token);
      localStorage.setItem("user_refresh_token", response.auth.refresh_token);
      const expiresAt = Date.now() + response.auth.expires_in * 1000;
      localStorage.setItem("user_token_expires", expiresAt.toString());
    }

    return response.user;
  }

  /**
   * 登出
   */
  async logout(): Promise<void> {
    await userApiClient.delete("/auth/logout");

    // 清除 Token（移动端）
    localStorage.removeItem("user_access_token");
    localStorage.removeItem("user_refresh_token");
    localStorage.removeItem("user_token_expires");
  }

  /**
   * 检查用户是否已登录
   * 通过尝试获取当前用户信息来判断
   */
  async checkAuth(): Promise<User | null> {
    try {
      return await this.getCurrentUser();
    } catch (error) {
      return null;
    }
  }
}

// 导出单例
export const userService = new UserService();
export default userService;
